<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="ayuda-header">
        <div class="ayuda-header-title">
          <font-awesome-icon icon="info-circle" class="ayuda-header-icon" />
          <div>
            <h3>Métodos de cálculo de población</h3>
            <span class="ayuda-header-sub">Geoproceso de influencia Puntual, Lineal y Pluma de Dispersión</span>
          </div>
        </div>
        <button class="btn-close" title="Cerrar (Esc)" @click="$emit('close')">
          <font-awesome-icon icon="times" />
        </button>
      </div>

      <div class="ayuda-body">
        <p class="intro">
          El Visor-GRD posee <b>dos métodos</b> para calcular la población
          potencialmente expuesta, resultante de un geoproceso de influencia
          puntual, lineal o pluma de dispersión. Usted puede ejecutar el
          geoproceso con la opción por defecto o cambiarla en consideración a la
          información que a continuación se entrega. No obstante debe tener
          presente que <b>habrá diferencias en los resultados de población
          expuesta según el método que se escoja</b>.
        </p>

        <div class="definiciones">
          <div class="definiciones-titulo">Definiciones importantes antes de comenzar</div>
          <p>
            <b>Entidad Rural:</b> conjunto de viviendas que conforman una aldea,
            caserío, caleta, etc., no urbana.
          </p>
          <p>
            <b>Ameba:</b> es el polígono que se traza alrededor de las viviendas
            cuyos habitantes dicen ser parte de una misma entidad rural y por
            tanto dicha ameba representa el territorio que abarca la entidad
            rural. Para efectos estadísticos, una ameba de entidad rural es el
            equivalente a una manzana urbana, dado que ambas son la unidad
            territorial mínima estadística en lo urbano y en lo rural
            respectivamente.
          </p>
          <figure>
            <img :src="imgAmebas" alt="Amebas de entidad rural" />
            <figcaption>Amebas de entidad rural</figcaption>
          </figure>
        </div>

        <div class="metodo-card">
          <div class="metodo-card-header">
            <span class="metodo-badge">Método N°1</span>
            <span class="metodo-subtitulo">Población como polígonos</span>
            <span class="metodo-chip">Por defecto</span>
          </div>
          <div class="metodo-card-body">
            <p>
              Tanto las manzanas urbanas como las amebas de entidad rural son
              entendidas por el sistema como polígonos y por tanto su atributo de
              “población” está “anclado” a la figura geométrica poligonal
              (fig. 1). Así, <b>todos los polígonos “tocados”</b> por un radio de
              influencia (área de amenaza) utilizado en el geoproceso para el
              análisis de exposición aportarán con su dato de población al
              resultado del análisis.
            </p>
            <figure>
              <img :src="imgFigura1" alt="Método de población como polígonos" />
              <figcaption>Figura N°1</figcaption>
            </figure>
            <p>
              El resultado de aplicar este método <b>suele maximizar</b> el total
              de población resultante, pues como se aprecia (fig. 1) bastará con
              que los polígonos (manzanas o amebas) sean “tocados” por los radios
              o áreas de influencia de la amenaza, en mayor o menor proporción,
              para que toda su población sea considerada en el análisis de
              población total expuesta. Esto es consecuencia del impedimento del
              sistema para calcular una “proporción” de población en función de
              la “proporción” en que el polígono es “tocado”.
            </p>
          </div>
        </div>

        <div class="metodo-card">
          <div class="metodo-card-header">
            <span class="metodo-badge">Método N°2</span>
            <span class="metodo-subtitulo">Población como puntos</span>
          </div>
          <div class="metodo-card-body">
            <p>
              Los atributos o información, tanto de manzanas urbanas como de
              amebas rurales, son almacenados en puntos o centroides que
              representan gráficamente la ubicación espacial de cada manzana o
              ameba. <b>Solo los centroides “tocados”</b> por el radio o área de
              influencia de una amenaza aportarán con su dato de población al
              resultado del análisis (fig. 2).
            </p>
            <figure>
              <img :src="imgFigura2" alt="Método de población como puntos o centroides" />
              <figcaption>Figura N°2</figcaption>
            </figure>
            <p>
              El resultado de aplicar este método <b>puede subestimar</b> el
              total de población resultante, pues como se aprecia (fig. 2) no
              basta con tocar los polígonos (manzanas o amebas), sino que es
              necesario que los centroides de estas sean contenidos en el área de
              amenaza para que su dato de población sea considerado en el
              análisis de población total expuesta.
            </p>
          </div>
        </div>

        <p>
          La herramienta de Geoprocesos del Visor-GRD permite realizar análisis
          de áreas de influencia en base a distancias o radios, los que usted
          puede elegir construir sobre puntos (Influencia Radial y Pluma de
          Dispersión) o sobre líneas (Influencia Lineal).
        </p>
        <figure>
          <img :src="imgSelector" alt="Selector de geoprocesos del Visor-GRD" />
          <figcaption>Herramienta de Geoprocesos del Visor-GRD</figcaption>
        </figure>

        <p class="nota">
          <b>Nota:</b> el método de procesar la población expresada gráficamente
          como polígonos es el método de cálculo por defecto usado por el
          Visor-GRD.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import imgAmebas from '@/assets/geoprocesos/ayuda-amebas-rurales.png';
import imgFigura1 from '@/assets/geoprocesos/ayuda-figura1-poligonos.png';
import imgSelector from '@/assets/geoprocesos/ayuda-selector-geoprocesos.png';
import imgFigura2 from '@/assets/geoprocesos/ayuda-figura2-centroides.png';

export default {
  name: 'MetodoCalculoAyuda',
  data() {
    return {
      imgAmebas,
      imgFigura1,
      imgSelector,
      imgFigura2,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(9, 30, 47, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ayuda-fade 0.15s ease-out;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 92%;
  max-width: 760px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  animation: ayuda-pop 0.18s ease-out;
}

@keyframes ayuda-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes ayuda-pop {
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ---------- header ---------- */
.ayuda-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #004b7a, #006aa8);
  color: #fff;
}

.ayuda-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ayuda-header-icon {
  font-size: 26px;
  flex-shrink: 0;
  opacity: 0.9;
}

.ayuda-header h3 {
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  color: #fff;
}

.ayuda-header-sub {
  display: block;
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
}

.btn-close {
  background: rgba(255, 255, 255, 0.12);
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* ---------- cuerpo ---------- */
.ayuda-body {
  overflow-y: auto;
  padding: 18px 22px 22px;
  font-size: 13.5px;
  line-height: 1.65;
  color: #333;
  text-align: justify;
}

.intro {
  margin-top: 0;
  font-size: 14px;
}

/* ---------- definiciones ---------- */
.definiciones {
  background: #f6f9fb;
  border: 1px solid #dde8ef;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 14px 0;
}

.definiciones-titulo {
  font-weight: 700;
  color: #004b7a;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.definiciones p {
  margin: 6px 0;
}

/* ---------- tarjetas de método ---------- */
.metodo-card {
  border: 1px solid #e3e8ec;
  border-radius: 8px;
  margin: 16px 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.metodo-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fafbfc;
  border-bottom: 1px solid #e3e8ec;
}

.metodo-badge {
  background: #004b7a;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.metodo-subtitulo {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.metodo-chip {
  margin-left: auto;
  background: #e7f3ea;
  color: #1e7b34;
  border: 1px solid #bfe3c8;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.metodo-card-body {
  padding: 12px 16px;
}

.metodo-card-body p {
  margin: 8px 0;
}

/* ---------- nota ---------- */
.nota {
  background: #fff8e6;
  border: 1px solid #f1e3b3;
  border-left: 4px solid #e6a817;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 4px;
}

/* ---------- figuras ---------- */
figure {
  margin: 12px auto;
  text-align: center;
  background: #fafafa;
  border: 1px solid #eceff1;
  border-radius: 8px;
  padding: 10px;
  max-width: 560px;
}

figure img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

figcaption {
  font-size: 12px;
  font-style: italic;
  color: #667;
  margin-top: 6px;
}
</style>
